/**
 * Persistance UTM first-party + relais vers le dataLayer GTM.
 *
 * GA4 ne garde utm_source/utm_campaign qu'au niveau de la session
 * (session_source/session_campaign) : perdus après un timeout d'inactivité,
 * un changement de jour, ou dès que l'utilisateur quitte le domaine (widget
 * Weezevent en iframe). On stocke donc les UTM nous-mêmes (localStorage,
 * 90 jours, premier-touch — on n'écrase que si l'URL apporte de nouveaux
 * UTM) et on les repousse comme variable dataLayer à chaque page, pour que
 * GTM puisse les attacher à l'événement de conversion (clic "Book"), quel
 * que soit l'état de la session GA4 à ce moment-là.
 *
 * `getStoredUtm()` est pur (pas d'effet de bord) et peut être importé
 * partout (ex. event-search/view.js pour ses redirections programmatiques).
 * L'init (capture URL + listener clic) ne doit tourner qu'UNE fois : elle
 * est déclenchée depuis scripts.js (bundle global), pas ici au niveau
 * module — sinon chaque bloc qui importe ce fichier la re-déclencherait
 * dans son propre bundle (webpack ne partage pas ces imports relatifs).
 */

const KEY        = 'ndg_utm';
const MAX_AGE_MS = 90 * 24 * 60 * 60 * 1000; // 90 jours
const UTM_KEYS   = [ 'utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content' ];

function readStored() {
	try {
		const raw = localStorage.getItem( KEY );
		if ( ! raw ) return null;

		const saved = JSON.parse( raw );
		if ( saved.date && Date.now() - new Date( saved.date ).getTime() > MAX_AGE_MS ) {
			localStorage.removeItem( KEY );
			return null;
		}
		return saved;
	} catch ( e ) {
		return null;
	}
}

function captureFromUrl() {
	const params = new URLSearchParams( window.location.search );
	const found  = {};
	let hasAny   = false;

	UTM_KEYS.forEach( ( key ) => {
		const value = params.get( key );
		if ( value ) {
			found[ key ] = value;
			hasAny = true;
		}
	} );

	if ( ! hasAny ) return;

	try {
		localStorage.setItem( KEY, JSON.stringify( {
			...found,
			landing_page: window.location.pathname,
			date: new Date().toISOString(),
		} ) );
	} catch ( e ) {}
}

export function getStoredUtm() {
	const saved = readStored();
	if ( ! saved ) return null;

	const utm = {};
	UTM_KEYS.forEach( ( key ) => {
		if ( saved[ key ] ) utm[ key ] = saved[ key ];
	} );
	return Object.keys( utm ).length ? utm : null;
}

// Ajoute les UTM stockés à une URL de sortie (si absents), pour la
// plateforme de destination si elle sait les exploiter.
export function appendStoredUtm( href ) {
	const utm = getStoredUtm();
	if ( ! utm ) return href;

	try {
		const url = new URL( href );
		Object.entries( utm ).forEach( ( [ key, value ] ) => {
			if ( ! url.searchParams.has( key ) ) url.searchParams.set( key, value );
		} );
		return url.toString();
	} catch ( e ) {
		return href;
	}
}

export function pushBookClickEvent() {
	window.dataLayer = window.dataLayer || [];
	window.dataLayer.push( { event: 'ndg_book_click', ndg_utm: getStoredUtm() || undefined } );
}

export function initUtmTracking() {
	if ( window.__ndgUtmInit ) return;
	window.__ndgUtmInit = true;

	captureFromUrl();

	window.dataLayer = window.dataLayer || [];
	const storedUtm = getStoredUtm();
	if ( storedUtm ) {
		window.dataLayer.push( { event: 'ndg_utm_ready', ndg_utm: storedUtm } );
	}

	// Capture phase : les liens du widget Weezevent appellent
	// event.stopPropagation() (voir modal.store.js), ce qui bloquerait un
	// listener délégué classique posé en bulle. En capture, on s'exécute
	// avant que ça ne se produise.
	document.addEventListener( 'click', ( e ) => {
		const el = e.target.closest( '[data-ndg-track="book"]' );
		if ( ! el ) return;

		pushBookClickEvent();

		// Lien externe (billetterie) uniquement — le bouton widget Weezevent
		// (sans href, ouvre une modale) n'est pas concerné.
		if ( el.tagName === 'A' && el.href ) {
			el.href = appendStoredUtm( el.href );
		}
	}, true );
}
